<!--
  Created 01.01.2024
  RichText editor component based on Quill.js
  https://vueup.github.io/vue-quill/ -- based on quill 1.2
  https://v1.quilljs.com/docs/quickstart
-->
<template>
  <div class="text-editable-rich-text relative">
    <label
      v-if="label"
      :for="richTextAreaKey"
      class="mb-2 block text-xs font-medium"
      :class="labelClass">{{ label }}</label>
    <QuillEditor
      :id="richTextAreaKey"
      v-model:content="editorValue"
      :options="{ bounds: '#main-content' }"
      theme="bubble"
      :toolbar="[
        [{'size':['small', false, 'large']}],
        ['bold','italic','underline','strike'],
        [{'list':'ordered'},{'list':'bullet'}],
        [{'indent':'-1'},{'indent':'+1'}],
        ['clean']
      ]"
      :read-only="disabled"
      :placeholder="placeholder"
      :tabindex="tabIndex"
      class="w-full resize-none whitespace-pre-wrap border-none p-0
      hover:cursor-text focus:border-purple-700 focus:ring-purple-700"
      :class="[
        {'hover:bg-gray-200 focus:bg-gray-100': backgroundStyle === 'white'},
        {'hover:bg-black/30 focus:bg-black/60': backgroundStyle === 'black'},
        {'hover:bg-black/10 focus:bg-black/20': backgroundStyle === 'gray'},
        {'bg-gray-200': changed && showButtons},
        {'hover:!cursor-not-allowed hover:!bg-transparent focus:!bg-transparent': disabled},
        textAreaClass
      ]"
      :style="[
        { minHeight: props.tabRows ? `${props.tabRows}lh` : '3lh' },
        { maxHeight: props.tabRowsMax ? `${props.tabRowsMax}lh` : 'unset' },
      ]"
      @ready="(editor) => onEditorReady(editor)"
      @focus="disabled ? null : edit()"
      @blur="disabled ? null : edited()"
      @textChange="disabled ? null : editing()"
      @keydown.esc="disabled ? null : cancel()" />
    <span
      v-if="!showButtons && changed"
      class="absolute -top-1 right-1 text-lg font-bold text-red-500">*</span>
    <div
      v-if="changed && showButtons"
      class="flex justify-end
      text-sm font-semibold"
      :class="buttonsWrapperClass">
      <button
        class="mr-0.5 rounded-bl-md border border-gray-200
        bg-gray-200 px-2 py-1 text-gray-500
        hover:border-gray-300 hover:bg-gray-300 hover:text-gray-700"
        :class="cancelButtonClass"
        @click="cancel">
        Cancel
      </button>
      <button
        class="rounded-br-md border border-purple-800
        bg-purple-800 px-2 py-1 text-white
        hover:border-purple-700 hover:bg-purple-700"
        :class="saveButtonClass"
        @click="save">
        Save changes
      </button>
    </div>
  </div>
</template>

<script setup>
/* external */
import {
  computed,
  ref, watch,
} from 'vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.bubble.css';
import 'quill-paste-smart';
import { useProposalStore } from '@/Stores/Proposal';

const props = defineProps({
  modelValue: {
    type: [Object, null],
    required: true,
    default: () => ({}),
  },
  placeholder: {
    type: String,
    required: false,
    default: 'Enter your text here and format it by selecting any part.',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  labelClass: {
    type: String,
    required: false,
    default: '',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  showButtons: {
    type: Boolean,
    required: false,
    default: true,
  },
  tabIndex: {
    type: Number,
    required: false,
    default: 0,
  },
  tabRows: {
    type: Number,
    required: false,
    default: 0,
  },
  tabRowsMax: {
    type: Number,
    required: false,
    default: 0,
  },
  cancelChanges: {
    type: Boolean,
    required: false,
    default: false,
  },
  savedChanges: {
    type: Boolean,
    required: false,
    default: false,
  },
  backgroundStyle: {
    type: String,
    required: false,
    default: 'white',
    validator: (value) => ['white', 'black', 'gray'].includes(value),
  },
  textAreaClass: {
    type: String,
    required: false,
    default: '',
  },
  buttonsWrapperClass: {
    type: String,
    required: false,
    default: '',
  },
  saveButtonClass: {
    type: String,
    required: false,
    default: '',
  },
  cancelButtonClass: {
    type: String,
    required: false,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'saved', 'editing', 'edited', 'cancelled']);

// Use Pinia proposal store
const store = useProposalStore();

const richTextAreaKey = Math.random().toString(36).substring(2, 15)
  + Math.random().toString(36).substring(2, 15);

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const editorValue = ref();
const changed = ref(false);
const oldValue = ref();

/**
 * When the Quill editor is ready, we set the contents to the modelValue
 * @param {object} editor - Quill editor instance
 */
function onEditorReady(editor) {
  oldValue.value = false;
  editor.setContents(modelValue.value);
}

/**
 * Set oldValue to current value if not changed
 */
function edit() {
  if (changed.value) {
    return;
  }
  oldValue.value = editorValue.value;
}

/**
 * Clear editing timeout, set changed to true and emit edited event data
 * @param {boolean} forceSave - Force save changes
 * @returns {Promise<void>}
 */
async function edited(forceSave = false) {
  // if forceSave is true or value is different from oldValue, proceed
  if (forceSave === true || oldValue.value !== editorValue.value) {
    changed.value = true;

    // Await the editing timer before emitting the edited event
    await store.setEditingTimer(() => {
      // Emit event if showButtons is false
      if (!props.showButtons) {
        modelValue.value = editorValue.value;
        emit('edited', modelValue.value);
      }
    }, 1500);
  }
}

/**
 * emit editing event, and finally emit edited event data
 */
function editing() {
  if (oldValue.value === false) {
    return;
  }
  store.setEditingTimer(
    () => {
      emit('editing', true);
      if (!props.showButtons) {
        oldValue.value = editorValue.value;
        edited(true);
      }
    },
    1500,
  );
}

/**
 * Cancel changes and emit cancelled event data
 */
function cancel() {
  changed.value = false;
  editorValue.value = oldValue.value;
  modelValue.value = oldValue.value;
  document.getElementById(richTextAreaKey).blur();
  oldValue.value = '';
  emit('cancelled', modelValue.value);
  setTimeout(() => {
    store.resetEditingTimer();
  }, 1);
}

/**
 * Saves changes and emit saved event data
 */
function save() {
  modelValue.value = editorValue.value;
  emit('update:modelValue', modelValue.value);
  emit('save', modelValue.value);
  changed.value = false;
}

watch(() => props.cancelChanges, () => {
  if (changed.value && props.cancelChanges) {
    cancel();
  }
});

watch(() => props.savedChanges, () => {
  if (changed.value && props.savedChanges) {
    changed.value = false;
  }
});

</script>
<style lang="scss">
  .text-editable-rich-text {
    .ql-container {
      height: inherit;
      font-family: inherit;
      color: inherit;
      .ql-editor {
        height: inherit;
        min-height: inherit;
        max-height: inherit;
        font-family: inherit;
        padding: inherit;
        font-size: inherit;
        line-height: inherit;
        color: inherit;
        .ql-size-small {
          font-size: 0.75em;
        }
        .ql-size-large {
          font-size: 1.25em;
          font-weight: 500;
        }
        strong.ql-size-large {
          font-weight: 700;
          letter-spacing: 0.01em;
        }
        &::before {
          left: 0;
          color: rgba(0,0,0,0.3);
        }
        &:focus {
          outline: 2px solid transparent;
          outline-offset: 2px;
          --tw-ring-inset: var(--tw-empty, );
          --tw-ring-offset-width: 0px;
          --tw-ring-offset-color: #fff;
          --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
          --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
          box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);
          --tw-ring-opacity: 1;
          --tw-ring-color: rgb(126 34 206 / var(--tw-ring-opacity));
          --tw-border-opacity: 1;
          border-color: rgb(126 34 206 / var(--tw-border-opacity));
        }
      }
    }
    .ql-container.ql-disabled .ql-editor * {
      cursor: not-allowed;
    }
  }
  .is-public {
    .ql-container.ql-disabled .ql-editor * {
      cursor: auto;
    }
  }
</style>
